import React from "react";
import { View, Text, StyleSheet } from "react-native";
import Svg, { Path, Circle, Rect, Line } from "react-native-svg";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import { Reveal, WebClass, FONT_DISPLAY, FONT_MONO } from "./_shared";

// =============================================================================
// Security & Compliance — Variant A from the mockup. Three blocks stacked:
//   1. Compliance badge wall (6 small bordered badges)
//   2. Three detail cards — Identity & access / Data protection / Deployment
//   3. Avaya Government Cloud partnership callout
// Every claim maps to evidence in the repo (case studies, BFF auth model,
// About page). No fabricated certifications.
// =============================================================================

// ---- Shared SVG primitives -------------------------------------------------
const stroke = { strokeWidth: 2, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };

function ShieldIcon({ color, size = 24 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path stroke={color} {...stroke} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </Svg>
  );
}
function CheckboxIcon({ color, size = 24 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path stroke={color} {...stroke} d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </Svg>
  );
}
function HomeIcon({ color, size = 24 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path stroke={color} {...stroke} d="M3 11l9-7 9 7M5 10v10h14V10" />
      <Path stroke={color} {...stroke} d="M9 22V12h6v10" />
    </Svg>
  );
}
function ClockIcon({ color, size = 24 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Circle stroke={color} {...stroke} cx={12} cy={12} r={10} />
      <Path stroke={color} {...stroke} d="M12 8v4l3 2" />
    </Svg>
  );
}
function ShieldCheckIcon({ color, size = 24 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path stroke={color} {...stroke} d="M12 2L3 7v6c0 5 4 9 9 10 5-1 9-5 9-10V7l-9-5z" />
      <Path stroke={color} {...stroke} d="M9 12l2 2 4-4" />
    </Svg>
  );
}
function LockIcon({ color, size = 24 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Rect stroke={color} {...stroke} x={3} y={11} width={18} height={11} rx={2} />
      <Path stroke={color} {...stroke} d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Svg>
  );
}
function UsersIcon({ color, size = 22 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path stroke={color} {...stroke} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <Circle stroke={color} {...stroke} cx={9} cy={7} r={4} />
      <Path stroke={color} {...stroke} d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </Svg>
  );
}
function ServerIcon({ color, size = 22 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Rect stroke={color} {...stroke} x={2} y={3} width={20} height={14} rx={2} />
      <Line stroke={color} {...stroke} x1={8} y1={21} x2={16} y2={21} />
      <Line stroke={color} {...stroke} x1={12} y1={17} x2={12} y2={21} />
    </Svg>
  );
}

// ---- Data ------------------------------------------------------------------
// `tone` controls the icon accent color: "brand" | "gov" | "gdpr".
const BADGES = [
  { id: "fedramp",  Icon: ShieldIcon,      name: "FedRAMP",       sub: "aligned",         tone: "gov"   },
  { id: "nist",     Icon: CheckboxIcon,    name: "NIST SP 800-171", sub: "cui controls",  tone: "brand" },
  { id: "karis",    Icon: HomeIcon,        name: "Kari's Law",    sub: "911 direct dial", tone: "gov"   },
  { id: "raybaum",  Icon: ClockIcon,       name: "RAY BAUM",      sub: "dispatchable loc.", tone: "gov" },
  { id: "e911",     Icon: ShieldCheckIcon, name: "E911",          sub: "NT integration",  tone: "brand" },
  { id: "gdpr",     Icon: LockIcon,        name: "GDPR / IMO",    sub: "maritime",        tone: "gdpr"  },
];

const DETAILS = [
  {
    id: "identity",
    Icon: UsersIcon,
    title: "Identity & access",
    desc:
      "Per-user JWT with single-use rotating refresh tokens. Granular role-based access control on every resource.",
    bullets: [
      "15 min access · 30 day refresh",
      "RBAC per resource (view / edit / delete)",
      "Group roles for support tiers & managers",
      "SSO / SAML on Enterprise",
    ],
  },
  {
    id: "data",
    Icon: LockIcon,
    title: "Data protection",
    desc:
      "Recording with retention policies, multi-tenant data boundaries, and full audit visibility per tenant.",
    bullets: [
      "On-demand call recording + retention",
      "Multi-tenant isolation (no shared state)",
      "Voicemail boundaries per extension",
      "Audit log per tenant",
    ],
  },
  {
    id: "deploy",
    Icon: ServerIcon,
    title: "Deployment models",
    desc:
      "Cloud, on-prem, or air-gapped. Same platform across all three. Hardware appliance the size of a pizza box for sensitive ops.",
    bullets: [
      "Cloud via Avaya Gov Cloud (FedRAMP-authorized)",
      "On-prem appliance (cruise / military / hardened ops)",
      "Air-gapped option for classified environments",
      "99.99% uptime SLA",
    ],
  },
];

const CALLOUT_TAGS = ["FEDRAMP CLOUD", "NIST 800-171", "AVAYA EDGE", "CSG-4 PROVEN"];

// ---- Component -------------------------------------------------------------
export default function NtVoxcraftSecurity() {
  const { theme } = useTheme();
  const isMobile = useMobile();

  const toneColor = (tone) =>
    tone === "gov" ? theme.orange : tone === "gdpr" ? theme.green : theme.brand2;

  return (
    <View nativeID="security" style={[styles.section, { paddingVertical: isMobile ? 64 : 110 }]}>
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>SECURITY & COMPLIANCE</Text>
            <Text style={[styles.title, { color: theme.text }, isMobile && styles.titleMobile]}>Built for regulated buyers.</Text>
            <Text style={[styles.titleSoft, { color: theme.textDim }, isMobile && styles.titleMobile]}>From small business to FedRAMP.</Text>
            <Text style={[styles.lede, { color: theme.textDim }]}>
              The same multi-tenant platform that runs cruise PBXs ships into Navy deployments and federal
              contracts. Compliance, isolation, and deployment flexibility are first-class, not bolt-ons.
            </Text>
          </View>
        </Reveal>

        {/* --- Compliance badge wall --- */}
        <View style={[styles.badgeStrip, isMobile && styles.badgeStripMobile]}>
          {BADGES.map((b, i) => {
            const color = toneColor(b.tone);
            return (
              <View key={b.id} style={[styles.badgeCell, isMobile && styles.badgeCellMobile]}>
                <Reveal delay={(i % 3) + 1} style={styles.revealFill}>
                  <WebClass
                    className="voxcraft-ind-card"
                    style={[styles.badge, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}
                  >
                    <b.Icon color={color} size={24} />
                    <Text style={[styles.badgeName, { color: theme.text }]}>{b.name}</Text>
                    <Text style={[styles.badgeSub, { color: theme.textMute }]}>{b.sub}</Text>
                  </WebClass>
                </Reveal>
              </View>
            );
          })}
        </View>

        {/* --- Detail cards --- */}
        <View style={[styles.detailGrid, isMobile && styles.detailGridMobile]}>
          {DETAILS.map((d, i) => (
            <View key={d.id} style={[styles.detailCell, isMobile && styles.detailCellMobile]}>
              <Reveal delay={(i % 3) + 1} style={styles.revealFill}>
                <WebClass
                  className="voxcraft-feat-card"
                  style={[styles.detailCard, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}
                >
                  <View style={[styles.detailIcon, { backgroundColor: theme.pillBg, borderColor: theme.pillBorder }]}>
                    <d.Icon color={theme.brand2} size={22} />
                  </View>
                  <Text style={[styles.detailTitle, { color: theme.text }]}>{d.title}</Text>
                  <Text style={[styles.detailDesc, { color: theme.textDim }]}>{d.desc}</Text>
                  <View style={[styles.bullets, { borderTopColor: theme.borderSoft }]}>
                    {d.bullets.map((line, j) => (
                      <View key={j} style={styles.bulletRow}>
                        <View style={[styles.bulletDot, { backgroundColor: theme.brand2 }]} />
                        <Text style={[styles.bulletText, { color: theme.textDim }]}>{line}</Text>
                      </View>
                    ))}
                  </View>
                </WebClass>
              </Reveal>
            </View>
          ))}
        </View>

        {/* --- Avaya Gov Cloud callout --- */}
        <Reveal>
          <View
            style={[
              styles.callout,
              { backgroundColor: theme.surface, borderColor: theme.borderSoft },
              isMobile && styles.calloutMobile,
            ]}
          >
            <View style={styles.calloutLeft}>
              <Text style={[styles.calloutEyebrow, { color: theme.orange }]}>⚑ AVAYA EDGE PARTNER PROGRAM</Text>
              <Text style={[styles.calloutTitle, { color: theme.text }]}>
                Deployed inside Avaya's FedRAMP-authorized Government Cloud.
              </Text>
              <Text style={[styles.calloutBody, { color: theme.textDim }]}>
                NT Connect is part of the Avaya Edge Partner Program. Voxcraft runs inside Avaya's
                Government Cloud, the same authorization boundary used by federal agencies for
                production communications.
              </Text>
            </View>
            <View style={[styles.calloutRight, isMobile && styles.calloutRightMobile]}>
              {CALLOUT_TAGS.map((tag) => (
                <View
                  key={tag}
                  style={[
                    styles.govTag,
                    { backgroundColor: "rgba(241,178,80,0.12)", borderColor: "rgba(241,178,80,0.4)" },
                  ]}
                >
                  <Text style={[styles.govTagText, { color: theme.orange }]}>{tag}</Text>
                </View>
              ))}
            </View>
          </View>
        </Reveal>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { paddingHorizontal: 28 },
  container: { maxWidth: 1280, marginHorizontal: "auto", width: "100%" },

  head: { maxWidth: 760, marginBottom: 56 },
  eyebrow: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6, marginBottom: 12, textTransform: "uppercase" },
  title:     { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "800", letterSpacing: -1, lineHeight: 46 },
  titleSoft: { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "500", letterSpacing: -1, lineHeight: 46, marginBottom: 16 },
  titleMobile: { fontSize: 32, lineHeight: 36 },
  lede: { fontSize: 17, lineHeight: 27 },

  // Compliance badge wall — 6 across, 3 across at narrow widths.
  badgeStrip: { flexDirection: "row", flexWrap: "wrap", alignItems: "stretch", marginHorizontal: -5, marginBottom: 56 },
  badgeStripMobile: { marginHorizontal: 0 },
  badgeCell: { width: "16.66%", padding: 5 },
  badgeCellMobile: { width: "50%", padding: 5 },
  revealFill: { flex: 1, width: "100%" },
  badge: {
    flex: 1,
    borderRadius: 12, borderWidth: 1,
    paddingVertical: 18, paddingHorizontal: 12,
    alignItems: "center", justifyContent: "center",
    minHeight: 110,
  },
  badgeName: {
    fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: "700",
    letterSpacing: -0.1, marginTop: 10, textAlign: "center",
  },
  badgeSub: {
    fontFamily: FONT_MONO, fontSize: 9.5,
    letterSpacing: 0.5, marginTop: 4, textTransform: "uppercase", textAlign: "center",
  },

  // Detail cards — 3 across, stack on mobile.
  detailGrid: { flexDirection: "row", flexWrap: "wrap", alignItems: "stretch", marginHorizontal: -9 },
  detailGridMobile: { flexDirection: "column", marginHorizontal: 0 },
  detailCell: { width: "33.33%", padding: 9 },
  detailCellMobile: { width: "100%", padding: 0, paddingBottom: 12 },
  detailCard: { flex: 1, borderRadius: 14, borderWidth: 1, padding: 28 },
  detailIcon: {
    width: 46, height: 46, borderRadius: 10, borderWidth: 1,
    alignItems: "center", justifyContent: "center", marginBottom: 18,
  },
  detailTitle: { fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: "700", letterSpacing: -0.2, marginBottom: 10 },
  detailDesc: { fontSize: 13.5, lineHeight: 21, marginBottom: 18 },
  bullets: { borderTopWidth: 1, paddingTop: 14 },
  bulletRow: { flexDirection: "row", alignItems: "center", paddingVertical: 5 },
  bulletDot: { width: 5, height: 5, borderRadius: 3, marginRight: 8 },
  bulletText: { fontSize: 12.5, flex: 1 },

  // Avaya callout
  callout: {
    marginTop: 56,
    borderRadius: 16, borderWidth: 1,
    padding: 36,
    flexDirection: "row", alignItems: "center", gap: 32,
  },
  calloutMobile: { flexDirection: "column", alignItems: "stretch", padding: 24, gap: 20 },
  calloutLeft: { flex: 1 },
  calloutEyebrow: {
    fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700",
    letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 10,
  },
  calloutTitle: { fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: "700", letterSpacing: -0.3, marginBottom: 8 },
  calloutBody: { fontSize: 13.5, lineHeight: 21, maxWidth: 460 },
  calloutRight: { flexDirection: "row", flexWrap: "wrap", gap: 10, justifyContent: "flex-end", maxWidth: 280 },
  calloutRightMobile: { justifyContent: "flex-start", maxWidth: "100%" },
  govTag: {
    paddingHorizontal: 13, paddingVertical: 7,
    borderRadius: 8, borderWidth: 1,
  },
  govTagText: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 0.6 },
});
